import React, { useEffect, useMemo } from 'react';
import { View, ActivityIndicator, Platform, Pressable } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Text } from '@/components/ui/text';
import { useTranslation } from '@/hooks/useTranslation';
import { ROUTES } from '@/navigation/routes';
import { wasCheckoutReturnConsumed } from '@/utils/checkoutCompletion';

export default function CheckoutCompleteScreen() {
  const { status, session_id: sessionId } = useLocalSearchParams<{ status?: string; session_id?: string }>();
  const router = useRouter();
  const { t } = useTranslation();
  const normalizedStatus = useMemo(() => {
    if (Array.isArray(status)) return status[0]?.toLowerCase();
    return (status || 'success').toLowerCase();
  }, [status]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      router.replace(ROUTES.TABS_DASHBOARD as any);
      return;
    }

    // Subscription checkout returning in the same tab (Stripe embedded
    // checkout's return_url on Testing). The root layout already recorded
    // the completion; `/` routes by the refreshed phase.
    const stripeSessionId = Array.isArray(sessionId) ? sessionId[0] : sessionId;
    if (!window.opener && (stripeSessionId?.startsWith('cs_') || wasCheckoutReturnConsumed())) {
      router.replace('/' as any);
      return;
    }

    try {
      window.opener?.postMessage(
        { type: 'credit_checkout_complete', status: normalizedStatus },
        window.location.origin
      );
    } catch {}

    const timer = window.setTimeout(() => {
      try {
        window.close();
      } catch {}
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [normalizedStatus, router, sessionId]);

  const title =
    normalizedStatus === 'cancel'
      ? t('common.cancel')
      : t('dashboard.purchaseSuccess');

  return (
    <View className="flex-1 items-center justify-center bg-gray-50 px-6">
      <View className="w-full max-w-md rounded-2xl border border-gray-200 bg-white px-6 py-8 items-center shadow-sm">
        <ActivityIndicator size="large" />
        <Text className="mt-4 text-lg font-semibold text-gray-900">{title}</Text>
        <Text className="mt-2 text-sm text-gray-500 text-center">
          {t('dashboard.purchaseProcessingMessage')}
        </Text>
        {Platform.OS === 'web' && (
          <Pressable
            onPress={() => {
              try {
                window.close();
              } catch {}
            }}
            className="mt-4 rounded-full bg-primary px-4 py-2"
          >
            <Text className="text-white font-semibold">{t('common.close')}</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}
